<script setup lang="ts">
import {DialogFooter,} from "@/core/components/ui/dialog";
import {Plus} from "@lucide/vue";
import AppointmentCalendar from "@/appointment/components/consumer-appointment-timeline/AppointmentCalendar.vue";
import {computed, ref} from "vue";
import {type DateValue} from "@internationalized/date";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {SupportProgramIndexResource} from "@/core/http/resources/SupportProgramResource";
import AppointmentItem from "@/appointment/components/consumer-appointment-timeline/AppointmentItem.vue";
import {storeToRefs} from "pinia";
import {useSystemStore} from "@/stores/system.store";
import {AppointmentCreateRequest} from "@/core/http/resources/AppointmentResource";
import {supportProgramApi, teamApi} from "@/container";
import {Appointment} from "@/core/interfaces/Appointment";
import useSwr from "@/core/composables/swr/useSwr";
import LegacyUser from "@/core/interfaces/User";
import {operatorsFromAccesses} from "@/agenda/lib/operators";
import {ProgramBookable} from "@/program/interfaces/ProgramBookable";
import {durationForBooker} from "@/appointment/lib/bookerAppointment";
import {isPastStart} from "@/agenda/lib/agendaTime";
import {PAST_APPOINTMENT_MESSAGE} from "@/agenda/constants";

export interface LegacyUserWithOperatorCode extends LegacyUser {
    operatorCode?: string;
}

export interface AppointmentItem {
    date: DateValue;
    startTime: string | undefined;
      supportProgram: SupportProgramIndexResource | null;
    bookable: ProgramBookable | null;
    memo?: string;
    /** Access point of the operator, as the select stores it (a string). */
    operator_access_point_id?: string;
}

export type PartialAppointmentItem = Omit<AppointmentItem, 'date'> & {
    date?: DateValue;
};

const props = defineProps<{
    /** ID of the customer for whom the appointments are being created */
    customerId: number;
    /** Optional ID of a support program to pre-select in the appointment items */
    supportProgramId?: number;
    /** Indicates if the component is in a loading state */
    loading: boolean;
}>();
const emits = defineEmits<{
    (e: 'submit', appointments: AppointmentCreateRequest[]): void;
    (e: 'batch-created', appointments: Appointment[]): void
}>();

const appointmentItems = ref<PartialAppointmentItem[]>([]);
const {environment} = storeToRefs(useSystemStore());
const {data: supportPrograms} = useSwr('support-programs?bookable=true', () => supportProgramApi().index({bookable: true}));
const {data: teamData} = useSwr('access/current/team', () => teamApi().index());

/*
 * Through the shared mapper rather than reading `environment.user` directly:
 * the API answers with a fabricated user for a member without an account, so
 * that read produced entries with a null id — unselectable, and one per
 * account-less member colliding on the same key.
 */
const availableOperators = computed(() => operatorsFromAccesses(teamData?.value?.team ?? []));

/**
 * Add an appointment item at a specific date or with no date.
 *
 * If supportProgramId prop is provided, the corresponding support program will be pre-selected.
 *
 * @param date
 */
const addAppointmentAtDate = (date: DateValue | undefined) => {
    const defaultSupportProgram = props.supportProgramId ?
        supportPrograms?.value?.find(program => program.id === props.supportProgramId) ?? null
        : null;

    appointmentItems.value.push({
        date: date,
        startTime: "10:00",
        supportProgram: defaultSupportProgram,
        bookable: null,
        operator_access_point_id: undefined
    });
};

const hasPastItem = computed(() => {
    return appointmentItems.value.some((item) => {
        return item.date && item.startTime && isPastStart(`${item.date.toString()}T${item.startTime}`);
    });
});

const disabled = computed(() => {
    return appointmentItems.value.some((item) => {
        return !item.startTime
            || !item.date;
    }) || hasPastItem.value || props.loading;
});

/**
 * Remove an item from the appointment items list.
 */
const removeItem = (index: number) => {
    appointmentItems.value.splice(index, 1);
};

/**
 * Map an AppointmentItem to AppointmentCreateRequest.
 *
 * @param item
 */
const mapItem = (item: AppointmentItem): AppointmentCreateRequest => {
    const dateStr = item.date.toString();
    const timeStr = item.startTime;

    const formattedDateTime = `${dateStr} ${timeStr}:00`;

    const appointmentable = item.supportProgram ? {
        appointmentable_type: 'support',
        appointmentable_id: item.supportProgram.id
    } : {};

    const duration = durationForBooker(item.bookable);

    return {
        title: item.bookable?.appointment_title ?? 'Rendez-vous santé',
        description: item.bookable?.appointment_description ?? 'Rendez-vous santé',
        customer_id: props.customerId,
        start_at: formattedDateTime,
        ...(item.bookable?.appointment_type_id ? {appointment_type_id: item.bookable.appointment_type_id} : {}),
        ...(duration !== undefined ? {duration} : {}),
        operator_access_point_id: item.operator_access_point_id
            ? Number(item.operator_access_point_id)
            : null,
        beneficiary_id: null,
        pharmacist_memo: item.memo,
        ...appointmentable
    };
};

/**
 * Emit the submit event with the mapped appointment items.
 */
const handleSubmit = (items: AppointmentItem[]) => {
    const organizationId = environment.value?.organizationId;

    if (!organizationId) {
        throw new Error('Organization ID is not defined in the environment.');
    }

    const data = items.map(mapItem);

    emits('submit', data);
};
</script>

<template>
  <div class="flex flex-col gap-3 bg-gray-50 px-6 pb-6">
    <div class="gap-3 flex flex-col">
      <AppointmentCalendar
        :model-value="undefined"
        class="w-[300px] mx-auto"
        @update:model-value="addAppointmentAtDate"
      />
      <div class="text-sm italic">
        Survolez les
        <div class="inline-block w-2 h-2 bg-primary rounded-full" />
        pour vérifier vos disponibilités
      </div>
    </div>
    <div v-if="appointmentItems.length" class="gap-y-2 flex flex-col">
      <AppointmentItem
        v-for="(item, index) in appointmentItems"
        :key="index"
        v-model:model-value="item as AppointmentItem"
        :available-support-programs="supportPrograms ?? []"
        :available-operators="availableOperators ?? []"
        @remove="removeItem(index)"
      />
    </div>
    <div class="bg-lightPurple-lightest p-3 text-primary rounded-lg flex items-center gap-3 justify-between">
      <p>Ajouter un RDV en sélectionnant un jour ou en cliquant ici</p>
      <BaseButton density="comfortable" @click="addAppointmentAtDate(undefined)">
        <Plus class="w-4 h-4 mr-1" />
        Ajouter
      </BaseButton>
    </div>
  </div>
  <DialogFooter class="py-4 px-6 bg-white items-center gap-3">
    <p v-if="hasPastItem" class="text-xs text-red-600">
      {{ PAST_APPOINTMENT_MESSAGE }}
    </p>
    <BaseButton
      class="self-end"
      primary
      density="comfortable"
      :loading="loading"
      :disabled="disabled || !appointmentItems?.length"
      @click="() => handleSubmit(appointmentItems as AppointmentItem[])"
    >
      Enregistrer
    </BaseButton>
  </DialogFooter>
</template>
